import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, GraduationCap, UserPlus, MessageCircle, Star, Check, AtSign } from "lucide-react";
import api from "@/lib/api";
import { Avatar, timeAgo } from "@/components/common";

const ICONS = { course: GraduationCap, join: UserPlus, comment: MessageCircle, review: Star, mention: AtSign };

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const ref = useRef(null);
  const navigate = useNavigate();

  const loadCount = () => api.get("/notifications/unread_count").then((r) => setCount(r.data.count)).catch(() => {});

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      const r = await api.get("/notifications");
      setItems(r.data);
    }
  };

  const markAll = async () => {
    await api.post("/notifications/read_all");
    setCount(0);
    setItems((it) => it.map((n) => ({ ...n, read: true })));
  };

  const click = async (n) => {
    if (!n.read) { await api.post(`/notifications/${n.notif_id}/read`); loadCount(); }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid="notification-bell" className="relative p-2 hover:bg-secondary/60 rounded-md transition-colors">
        <Bell size={18} />
        {count > 0 && (
          <span data-testid="notification-count"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 grid place-items-center text-[10px] font-bold text-white bg-cobalt rounded-full">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute left-0 mt-2 w-80 max-w-[calc(100vw-2rem)] max-h-[70vh] overflow-y-auto bg-popover border border-border rounded-xl shadow-xl z-[60]" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border sticky top-0 bg-popover">
            <span className="font-serif text-lg font-bold">Notifications</span>
            <button onClick={markAll} data-testid="mark-all-read" className="inline-flex items-center gap-1 text-xs text-cobalt hover:underline">
              <Check size={13} /> Mark all read
            </button>
          </div>
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">You're all caught up.</div>
          ) : (
            items.map((n) => {
              const Icon = ICONS[n.type] || Bell;
              return (
                <button key={n.notif_id} onClick={() => click(n)} data-testid={`notification-${n.notif_id}`}
                  className={`w-full text-left flex gap-3 px-4 py-3 border-b border-border/60 hover:bg-secondary/50 transition-colors ${!n.read ? "bg-secondary/40" : ""}`}>
                  {n.actor?.picture || n.actor?.name ? (
                    <Avatar src={n.actor.picture} name={n.actor.name} size={34} />
                  ) : (
                    <span className="grid place-items-center w-[34px] h-[34px] rounded-full bg-secondary text-cobalt"><Icon size={16} /></span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm leading-snug">{n.text}</p>
                    <span className="text-xs text-muted-foreground">{timeAgo(n.created_at)}</span>
                  </div>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-cobalt mt-1.5 shrink-0" />}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
